import React, { useCallback, useEffect, useState } from "react";
import { css } from "@emotion/css";
import { Add as AddIcon } from "@mui/icons-material";
import { Alert, Box, Button, Chip, CircularProgress, Grid, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { ProductPlatform, type Subscription } from "client/api-v2";
import type { AllowedIpPrefixDto, CloudSubscriptionDto } from "client/api/CloudSubscriptionApi";
import { getCloudSubscriptionById } from "client/api/CloudSubscriptionApi";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { PolicyAuthorized } from "components/authorization/PolicyAuthorized";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { CloudSubscriptionActivateIpAddressAllowListDialog } from "components/cloud-subscriptions/CloudSubscriptionActivateIpAddressAllowListDialog";
import { CloudSubscriptionDeleteAllowedIpAddressDialog } from "components/cloud-subscriptions/CloudSubscriptionDeleteAllowedIpAddressDialog";
import { CloudSubscriptionUpdateAllowedIpAddressDialog } from "components/cloud-subscriptions/CloudSubscriptionUpdateAllowedIpAddressDialog";
import { getCloudSubscriptionBySubscriptionId } from "../services/subscriptionService";

export function SubscriptionIpAddressAllowList() {
  const { entity: subscription } = useEntity<Subscription>();

  const [cloudSubscription, setCloudSubscription] = useState<CloudSubscriptionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showActivateIpAddressAllowListDialog, setShowActivateIpAddressAllowListDialog] = useState(false);
  const [showAddNewAllowedIpAddressDialog, setShowAddNewAllowedIpAddressDialog] = useState(false);

  const isCloudPlatform = subscription?.plan?.productPlatform === ProductPlatform.OctopusCloud;

  const loadCloudSubscription = useCallback(async () => {
    if (!subscription?.id || !isCloudPlatform) return;
    const cloudSub = await getCloudSubscriptionBySubscriptionId(subscription.id);
    // Re-fetch via the legacy API so we get the strongly-typed DTO the dialogs expect.
    const typed = await getCloudSubscriptionById(cloudSub.id);
    setCloudSubscription(typed);
  }, [subscription?.id, isCloudPlatform]);

  useEffect(() => {
    if (!subscription?.id) {
      setLoading(false);
      return;
    }
    if (!isCloudPlatform) {
      setLoading(false);
      setError("IP address allow list is only available for Cloud subscriptions.");
      return;
    }

    setLoading(true);
    setError(null);
    loadCloudSubscription()
      .catch((err) => {
        console.error("Failed to fetch cloud subscription:", err);
        setError("Failed to load IP address allow list.");
      })
      .finally(() => setLoading(false));
  }, [subscription?.id, isCloudPlatform, loadCloudSubscription]);

  const refresh = async () => {
    await loadCloudSubscription();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !cloudSubscription) {
    return <Alert severity="error">{error ?? "Unable to load IP address allow list."}</Alert>;
  }

  const ipAddressAllowList = cloudSubscription.ipAddressAllowList;
  const sortedAllowedIpPrefixes = sortIpPrefixes(ipAddressAllowList.allowedIpPrefixes);

  return (
    <DetailsPageLayout pageName={"IP address allow list"}>
      <Grid container spacing={3}>
        <Grid size={6}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 0.5,
            }}
          >
            Status
          </Typography>
          <Chip
            label={ipAddressAllowList.active ? "Active" : "Deactivated"}
            color={ipAddressAllowList.active ? "success" : "default"}
            size="small"
          />
        </Grid>
        <Grid
          size={6}
          sx={{
            textAlign: "right",
          }}
        >
          <PolicyAuthorized policies={RbacPolicies.CloudSubscriptions.ReadWrite}>
            <Button onClick={() => setShowActivateIpAddressAllowListDialog(true)} color="success" variant="contained">
              {ipAddressAllowList.active ? "Deactivate" : "Activate"}
            </Button>
          </PolicyAuthorized>
        </Grid>
        {ipAddressAllowList.active && sortedAllowedIpPrefixes.length === 0 && (
          <Grid size={12}>
            <Typography
              sx={{
                fontStyle: "italic",
              }}
            >
              Only Trusted Octopus Services are allowed access.
            </Typography>
          </Grid>
        )}
        {!ipAddressAllowList.active && sortedAllowedIpPrefixes.length === 0 && (
          <Grid size={12}>
            <Typography
              sx={{
                fontStyle: "italic",
              }}
            >
              All internet traffic is allowed access.
            </Typography>
          </Grid>
        )}
        <Grid container size={12}>
          <Stack spacing={1} sx={{ width: "100%" }}>
            {sortedAllowedIpPrefixes.map((ipAddress) => (
              <AllowedIpAddress
                key={ipAddress.prefix}
                cloudSubscriptionId={cloudSubscription.id}
                allowListActive={ipAddressAllowList.active}
                hasSingleAllowedIp={sortedAllowedIpPrefixes.length === 1}
                allowedIp={ipAddress}
                onChange={refresh}
              />
            ))}
          </Stack>
        </Grid>
        <PolicyAuthorized policies={RbacPolicies.CloudSubscriptions.ReadWrite}>
          <Grid size={12}>
            <Button
              onClick={() => setShowAddNewAllowedIpAddressDialog(true)}
              color="secondary"
              variant="outlined"
              startIcon={<AddIcon />}
            >
              Add a new IP address or range
            </Button>
          </Grid>
        </PolicyAuthorized>
      </Grid>
      <CloudSubscriptionActivateIpAddressAllowListDialog
        cloudSubscriptionId={cloudSubscription.id}
        ipAddressAllowList={ipAddressAllowList}
        activate={!ipAddressAllowList.active}
        open={showActivateIpAddressAllowListDialog}
        onConfirm={async () => {
          setShowActivateIpAddressAllowListDialog(false);
          await refresh();
        }}
        onClose={() => setShowActivateIpAddressAllowListDialog(false)}
      />
      <CloudSubscriptionUpdateAllowedIpAddressDialog
        cloudSubscriptionId={cloudSubscription.id}
        allowListActive={ipAddressAllowList.active}
        open={showAddNewAllowedIpAddressDialog}
        onConfirm={async () => {
          setShowAddNewAllowedIpAddressDialog(false);
          await refresh();
        }}
        onClose={() => setShowAddNewAllowedIpAddressDialog(false)}
      />
    </DetailsPageLayout>
  );
}

function AllowedIpAddress({
  cloudSubscriptionId,
  allowListActive,
  hasSingleAllowedIp,
  allowedIp,
  onChange,
}: {
  cloudSubscriptionId: string;
  allowListActive: boolean;
  hasSingleAllowedIp: boolean;
  allowedIp: AllowedIpPrefixDto;
  onChange: () => Promise<void>;
}) {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  return (
    <Grid
      container
      sx={{
        alignItems: "center",
      }}
    >
      <Grid
        size={{
          xs: 12,
          sm: 3,
        }}
      >
        <code className={codeTextStyles}>{allowedIp.prefix}</code>
      </Grid>
      <Grid size={7}>{allowedIp.description}</Grid>
      <Grid
        size={2}
        sx={{
          textAlign: "right",
        }}
      >
        <PolicyAuthorized policies={RbacPolicies.CloudSubscriptions.ReadWrite}>
          <Button onClick={() => setShowEditDialog(true)} size="small">
            Edit
          </Button>
          <Button onClick={() => setShowDeleteDialog(true)} size="small" sx={{ ml: 2 }}>
            Delete
          </Button>
        </PolicyAuthorized>
      </Grid>
      <CloudSubscriptionUpdateAllowedIpAddressDialog
        cloudSubscriptionId={cloudSubscriptionId}
        allowListActive={allowListActive}
        existingIpAddress={allowedIp}
        open={showEditDialog}
        onConfirm={async () => {
          setShowEditDialog(false);
          await onChange();
        }}
        onClose={() => setShowEditDialog(false)}
      />
      <CloudSubscriptionDeleteAllowedIpAddressDialog
        cloudSubscriptionId={cloudSubscriptionId}
        allowListActive={allowListActive}
        hasSingleAllowedIp={hasSingleAllowedIp}
        allowedIp={allowedIp}
        open={showDeleteDialog}
        onConfirm={async () => {
          setShowDeleteDialog(false);
          await onChange();
        }}
        onClose={() => setShowDeleteDialog(false)}
      />
    </Grid>
  );
}

function sortIpPrefixes(prefixes: AllowedIpPrefixDto[]) {
  return [...prefixes].sort((a, b) => {
    const [ipA, prefixA] = a.prefix.split("/");
    const [ipB, prefixB] = b.prefix.split("/");

    if (!ipA || !prefixA || !ipB || !prefixB) {
      return a.prefix.localeCompare(b.prefix);
    }

    const numA = ipToNumber(ipA);
    const numB = ipToNumber(ipB);

    if (numA !== numB) {
      return numA - numB;
    }

    return parseInt(prefixA, 10) - parseInt(prefixB, 10);
  });
}

function ipToNumber(ip: string) {
  return ip.split(".").reduce((acc, octet) => acc * 256 + parseInt(octet, 10), 0);
}

const codeTextStyles = css`
  margin: 0;
  padding: 0.1rem;
`;
